// User-defined attributes: asking the server which attributes APPLY.
//
// `useUdaResolve` is the core: a debounced `uda_resolve` call through the
// screen's execute funnel, guarded by a sequence token (the TxnScreen
// `protectSeedSeq` pattern) so an answer that comes back after a newer request
// was issued is dropped. `useUdaRecord` (one record: the header, or a line in
// its editor) and `useUdaRows` (a page of lines) drive it from the values on
// screen: they re-ask only when a DECIDING field changes (the answer's
// `deciding_fields`; `header.<col>` names a header value), never per
// keystroke.
//
// Inert without a UdaScreenProvider (no `runtime.uda` on this host) and for a
// form the screen knows has no layer: no request is made at all.
//
// Errors: a failed answer for a form KNOWN to have attributes keeps the last
// columns and reports `error` (the screen shows a quiet notice; the record
// stays usable — the server re-decides at save anyway, plan D6). A failed
// FIRST probe of a form with no evidence of a layer is taken as "none here"
// (logged at debug), so a host that does not serve the action yet — the J2EE
// runtime before Phase 7 — costs one request per form, never a notice.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parseApiError } from '../txn/utils'
import { answerHasLayer, useUdaScreen, type UdaScreenApi } from './context'
import type {
  UdaColumnDef, UdaFlag, UdaResolution, UdaResolveRequest, UdaRowResolution, UdaSetInfo,
} from './types'

/** How long the values must be still before a deciding-field change re-asks. */
export const UDA_RESOLVE_DEBOUNCE_MS = 300

export interface UdaResolveState {
  /** An answer has arrived (and was not dropped). */
  resolved: boolean
  defVersion: number
  columns: UdaColumnDef[]
  sets: UdaSetInfo[]
  flags: UdaFlag[]
  decidingFields: string[]
  /** Record scope: the deciding values. */
  drivers: Record<string, string | null>
  /** Rows scope: each row's resolution by its `_key`. */
  rows: Map<string, UdaRowResolution>
  loading: boolean
  error: string | null
}

const EMPTY_ROWS = new Map<string, UdaRowResolution>()
export const EMPTY_UDA_STATE: UdaResolveState = Object.freeze({
  resolved: false, defVersion: 0, columns: [], sets: [], flags: [], decidingFields: [],
  drivers: {}, rows: EMPTY_ROWS, loading: false, error: null,
}) as UdaResolveState

type RequestBody = Omit<UdaResolveRequest, 'form_no'>

const norm = (k: string): string => String(k || '').trim().replace(/\s+/g, '_').toLowerCase()

function valueOf(bag: Record<string, unknown> | null | undefined, key: string): unknown {
  if (!bag) return null
  if (key in bag) return bag[key] ?? null
  const n = norm(key)
  if (n in bag) return bag[n] ?? null
  // A record keyed by another spelling (db_name, a raw name) of the field.
  const hit = Object.keys(bag).find(k => norm(k) === n)
  return hit !== undefined ? (bag[hit] ?? null) : null
}

/** Field values for the request body: internal keys and nested rows out. */
export function udaPayloadValues(values: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(values || {})) {
    if (k.startsWith('_')) continue
    out[k] = v
  }
  return out
}

function decidingValue(field: string, values: Record<string, unknown> | null | undefined,
  headerValues: Record<string, unknown> | null | undefined): unknown {
  return field.startsWith('header.')
    ? valueOf(headerValues, field.slice('header.'.length))
    : valueOf(values, field)
}

function safeJson(v: unknown): string {
  try { return JSON.stringify(v) } catch { return String(v) }
}

/** The signature a record is re-asked on: its deciding values (+ identity). */
export function recordSignature(
  values: Record<string, unknown> | null | undefined,
  headerValues: Record<string, unknown> | null | undefined,
  fields: readonly string[], txnId: string | null | undefined, nonce: unknown,
): string {
  return safeJson([txnId || '', nonce ?? '', fields.map(f => decidingValue(f, values, headerValues))])
}

/** The core hook: a resolve for ONE form of the screen. */
export function useUdaResolve(formNo: string, apiOverride?: UdaScreenApi | null) {
  // The screen itself (which PROVIDES the store) passes it in; everything
  // below the screen reads it from the context.
  const ctxApi = useUdaScreen()
  const api = apiOverride !== undefined ? apiOverride : ctxApi
  const [state, setState] = useState<UdaResolveState>(EMPTY_UDA_STATE)
  const seqRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  useEffect(() => () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current)
    seqRef.current += 1
  }, [])

  const refresh = useCallback((
    req: RequestBody,
    opts: {
      delay?: number
      onAnswer?: (res: UdaResolution) => void
      /** False for a LIST answer: it carries only promoted columns and flags,
       *  so an empty one says nothing about the form's layer. */
      learnsLayer?: boolean
    } = {},
  ) => {
    if (!api || api.layerKnown(formNo) === false) return
    if (timerRef.current != null) { window.clearTimeout(timerRef.current); timerRef.current = null }
    const seq = ++seqRef.current
    const run = async () => {
      const learns = opts.learnsLayer !== false
      const probing = learns && api.layerKnown(formNo) === undefined
      setState(s => (s.loading ? s : { ...s, loading: true }))
      try {
        const res = await api.resolve({ ...req, form_no: formNo })
        if (seq !== seqRef.current) return
        const has = answerHasLayer(res)
        if (learns) api.noteLayer(formNo, has)
        if (!has) { setState(EMPTY_UDA_STATE); return }
        api.noteColumns(formNo, res.columns)
        opts.onAnswer?.(res)
        setState({
          resolved: true,
          defVersion: res.def_version,
          columns: res.columns,
          sets: res.sets,
          flags: res.flags,
          decidingFields: res.deciding_fields,
          drivers: res.drivers || {},
          rows: new Map(res.rows.map(r => [String(r._key), r])),
          loading: false,
          error: null,
        })
      } catch (err) {
        if (seq !== seqRef.current) return
        if (probing || !learns) {
          // No evidence this form has attributes: "none here", quietly.
          if (probing) api.noteLayer(formNo, false)
          if (typeof console !== 'undefined') {
            console.debug(`uda_resolve unavailable for form ${formNo} (treated as no additional attributes):`, err)
          }
          setState(EMPTY_UDA_STATE)
          return
        }
        const { message } = parseApiError(err)
        if (typeof console !== 'undefined') console.warn('uda_resolve failed:', message, err)
        setState(s => ({ ...s, loading: false, error: message || 'The additional details could not be loaded' }))
      }
    }
    const delay = opts.delay ?? 0
    if (delay > 0) {
      timerRef.current = window.setTimeout(() => { timerRef.current = null; void run() }, delay)
    } else {
      void run()
    }
  }, [api, formNo])

  const clear = useCallback(() => {
    if (timerRef.current != null) { window.clearTimeout(timerRef.current); timerRef.current = null }
    seqRef.current += 1
    setState(s => (s === EMPTY_UDA_STATE ? s : EMPTY_UDA_STATE))
  }, [])

  return { api, state, refresh, clear }
}

export interface UdaRecordOptions {
  formNo: string
  /** The record's values as on screen (form keys). */
  values: Record<string, unknown>
  /** The parent (header) values, for a line's `header.<col>` deciders. */
  headerValues?: Record<string, unknown> | null
  /** The saved record (viewers are served with `load` rights). */
  txnId?: string | null
  /** False while no record is open — nothing is asked. */
  active: boolean
  /** Bump to re-ask with unchanged values (after a save). */
  nonce?: unknown
  /** The store, for the component that provides it (TxnScreen). */
  api?: UdaScreenApi | null
}

export type UdaRecordState = UdaResolveState & { retry: () => void }

/** Which attributes apply to ONE record (record scope). */
export function useUdaRecord(opts: UdaRecordOptions): UdaRecordState {
  const { formNo, values, headerValues, txnId, active, nonce } = opts
  const { api, state, refresh, clear } = useUdaResolve(formNo, opts.api)
  const latest = useRef(opts)
  latest.current = opts
  const lastSig = useRef<string | null>(null)
  const [retryN, setRetryN] = useState(0)
  const on = active && !!api
  const sig = on
    ? `${retryN}|${recordSignature(values, headerValues, state.decidingFields, txnId, nonce)}`
    : null

  useEffect(() => {
    if (sig === null || !api) {
      if (lastSig.current !== null) { lastSig.current = null; clear() }
      return
    }
    if (api.layerKnown(formNo) === false) return
    if (sig === lastSig.current) return
    const first = lastSig.current === null
    lastSig.current = sig
    const cur = latest.current
    const reqValues = cur.values
    const reqHeader = cur.headerValues
    const reqTxn = cur.txnId
    const reqNonce = cur.nonce
    const reqRetry = retryN
    refresh({
      scope: 'record',
      header: udaPayloadValues(reqValues),
      header_values: udaPayloadValues(reqHeader || {}),
      ...(reqTxn ? { txn_id: String(reqTxn) } : {}),
    }, {
      delay: first ? 0 : UDA_RESOLVE_DEBOUNCE_MS,
      // The answer names the deciding fields: what this request covered is
      // the signature over THOSE, so learning them never re-asks by itself.
      onAnswer: (res) => {
        lastSig.current = `${reqRetry}|${recordSignature(reqValues, reqHeader, res.deciding_fields, reqTxn, reqNonce)}`
      },
    })
  }, [sig, api, formNo, refresh, clear, retryN])

  const retry = useCallback(() => setRetryN(n => n + 1), [])
  return useMemo(() => ({ ...(on ? state : EMPTY_UDA_STATE), retry }), [on, state, retry])
}

export interface UdaRowsOptions<R extends Record<string, unknown>> {
  formNo: string
  rows: readonly R[]
  /** A row's stable key (echoed back as `_key`). */
  rowKey: (row: R, index: number) => string
  headerValues?: Record<string, unknown> | null
  /** The saved HEADER record the lines belong to. */
  txnId?: string | null
  active: boolean
  nonce?: unknown
}

export type UdaRowsState = UdaResolveState & {
  /** A row's resolution (null while unknown). */
  rowOf: (key: string) => UdaRowResolution | null
  retry: () => void
}

/** Which attributes apply to each line of a page (rows scope). */
export function useUdaRows<R extends Record<string, unknown>>(opts: UdaRowsOptions<R>): UdaRowsState {
  const { formNo, rows, rowKey, headerValues, txnId, active, nonce } = opts
  const { api, state, refresh, clear } = useUdaResolve(formNo)
  const latest = useRef(opts)
  latest.current = opts
  const lastSig = useRef<string | null>(null)
  const [retryN, setRetryN] = useState(0)
  const on = active && !!api

  const sigOf = useCallback((list: readonly R[], hv: Record<string, unknown> | null | undefined,
    fields: readonly string[], txn: string | null | undefined, n: unknown, r: number) => {
    const hFields = fields.filter(f => f.startsWith('header.'))
    const rFields = fields.filter(f => !f.startsWith('header.'))
    return safeJson([r, txn || '', n ?? '', hFields.map(f => decidingValue(f, null, hv)),
      list.map((row, i) => (row._deleted ? null
        : [rowKey(row, i), ...rFields.map(f => decidingValue(f, row, hv))])).filter(Boolean)])
  }, [rowKey])

  const live = rows.some(r => !r._deleted)
  const sig = on && live ? sigOf(rows, headerValues, state.decidingFields, txnId, nonce, retryN) : null

  useEffect(() => {
    if (!on || !api) {
      if (lastSig.current !== null) { lastSig.current = null; clear() }
      return
    }
    if (sig === null) return  // no lines: nothing to ask, the last answer stays
    if (api.layerKnown(formNo) === false) return
    if (sig === lastSig.current) return
    const first = lastSig.current === null
    lastSig.current = sig
    const cur = latest.current
    const fields = state.decidingFields
    const rFields = fields.filter(f => !f.startsWith('header.'))
    const reqRows = cur.rows
    const body = reqRows.flatMap((row, i) => {
      if (row._deleted) return []
      const key = cur.rowKey(row, i)
      // Once the deciding fields are known only they travel; the first
      // probe sends the line as it is.
      const vals = rFields.length || state.resolved
        ? Object.fromEntries(rFields.map(f => [f, decidingValue(f, row, cur.headerValues)]))
        : udaPayloadValues(row)
      return [{ ...vals, _key: key }]
    })
    const reqHeader = cur.headerValues
    const reqTxn = cur.txnId
    const reqNonce = cur.nonce
    const reqRetry = retryN
    refresh({
      scope: 'rows',
      rows: body,
      header_values: udaPayloadValues(reqHeader || {}),
      ...(reqTxn ? { txn_id: String(reqTxn) } : {}),
    }, {
      delay: first ? 0 : UDA_RESOLVE_DEBOUNCE_MS,
      onAnswer: (res) => {
        lastSig.current = sigOf(reqRows, reqHeader, res.deciding_fields, reqTxn, reqNonce, reqRetry)
      },
    })
  }, [sig, on, api, formNo, refresh, clear, state.decidingFields, state.resolved, sigOf, retryN])

  const retry = useCallback(() => setRetryN(n => n + 1), [])
  const rowOf = useCallback((key: string) => state.rows.get(String(key)) || null, [state.rows])
  return useMemo(() => ({ ...(on ? state : EMPTY_UDA_STATE), rowOf, retry }), [on, state, rowOf, retry])
}

/** Promoted header attributes + flags for a LIST (scope "list"): asked once
 *  per layer version, and only when the list response said the form has a
 *  layer (`_uda`). */
export function useUdaList(opts: {
  formNo: string; defVersion: number | null; active: boolean; api?: UdaScreenApi | null
}): UdaResolveState {
  const { formNo, defVersion, active } = opts
  const { api, state, refresh, clear } = useUdaResolve(formNo, opts.api)
  const asked = useRef<number | null>(null)
  useEffect(() => {
    if (!active || !api || defVersion === null) {
      if (asked.current !== null) { asked.current = null; clear() }
      return
    }
    if (asked.current === defVersion) return
    asked.current = defVersion
    refresh({ scope: 'list' }, { learnsLayer: false })
  }, [active, api, defVersion, refresh, clear])
  return active ? state : EMPTY_UDA_STATE
}
